/**
 * Risk Level Classification Utility
 *
 * Provides standardized risk level classification with consistent
 * color coding and text formatting across the application.
 */

// Risk level configuration with standardized ranges
const RISK_LEVELS = {
  LOW: {
    min: 0.0,
    max: 1.67,
    label: "Low",
    color: "#10b981", // Green
    key: "low",
  },
  MODERATE: {
    min: 1.68,
    max: 3.33,
    label: "Medium",
    color: "#f59e0b", // Orange
    key: "medium",
  },
  HIGH: {
    min: 3.34,
    max: 5.0,
    label: "High",
    color: "#ef4444", // Red
    key: "high",
  },
};

/**
 * Determines the risk level category based on input value
 * @param {number|string} riskValue - The risk value to classify
 * @returns {Object} Risk level information with color, text, and key
 */
export const getRiskLevelInfo = (riskValue) => {
  // Handle null, undefined, or empty values
  if (riskValue === null || riskValue === undefined || riskValue === "") {
    return {
      level: "--",
      key: "unknown",
      color: "#6b7280", // Gray for unknown/missing data
      isValid: false,
    };
  }

  // Handle string inputs
  if (typeof riskValue === "string") {
    const trimmedValue = riskValue.trim().toLowerCase();

    // Handle explicit string values
    switch (trimmedValue) {
      case "low":
        return {
          level: RISK_LEVELS.LOW.label,
          key: RISK_LEVELS.LOW.key,
          color: RISK_LEVELS.LOW.color,
          isValid: true,
        };
      case "moderate":
      case "medium": // Support legacy 'medium' values
        return {
          level: RISK_LEVELS.MODERATE.label,
          key: RISK_LEVELS.MODERATE.key,
          color: RISK_LEVELS.MODERATE.color,
          isValid: true,
        };
      case "high":
        return {
          level: RISK_LEVELS.HIGH.label,
          key: RISK_LEVELS.HIGH.key,
          color: RISK_LEVELS.HIGH.color,
          isValid: true,
        };
      case "--":
      case "n/a":
      case "na":
      case "unknown":
        return {
          level: "--",
          key: "unknown",
          color: "#6b7280",
          isValid: false,
        };
      default: {
        // Try to parse as number
        const numericValue = parseFloat(trimmedValue);
        if (!isNaN(numericValue)) {
          return getRiskLevelInfo(numericValue);
        }
        // If not parseable, return unknown
        return {
          level: "--",
          key: "unknown",
          color: "#6b7280", // Gray
          isValid: false,
        };
      }
    }
  }

  // Handle numeric inputs
  if (typeof riskValue === "number" && !isNaN(riskValue)) {
    // Clamp value to valid range (0-5)
    const clampedValue = Math.max(0, Math.min(5, riskValue));

    // Determine risk level based on standardized ranges
    if (
      clampedValue >= RISK_LEVELS.LOW.min &&
      clampedValue <= RISK_LEVELS.LOW.max
    ) {
      return {
        level: RISK_LEVELS.LOW.label,
        key: RISK_LEVELS.LOW.key,
        color: RISK_LEVELS.LOW.color,
        isValid: true,
      };
    } else if (
      clampedValue >= RISK_LEVELS.MODERATE.min &&
      clampedValue <= RISK_LEVELS.MODERATE.max
    ) {
      return {
        level: RISK_LEVELS.MODERATE.label,
        key: RISK_LEVELS.MODERATE.key,
        color: RISK_LEVELS.MODERATE.color,
        isValid: true,
      };
    } else if (
      clampedValue >= RISK_LEVELS.HIGH.min &&
      clampedValue <= RISK_LEVELS.HIGH.max
    ) {
      return {
        level: RISK_LEVELS.HIGH.label,
        key: RISK_LEVELS.HIGH.key,
        color: RISK_LEVELS.HIGH.color,
        isValid: true,
      };
    }
  }

  // Fallback for any unhandled cases
  return {
    level: "--",
    key: "unknown",
    color: "#6b7280",
    isValid: false,
  };
};

/**
 * Legacy compatibility function - returns just the risk level text
 * @param {number|string} riskValue - The risk value to classify
 * @returns {string} Risk level text (capitalized)
 */
export const getRiskLevel = (riskValue) => {
  const riskInfo = getRiskLevelInfo(riskValue);
  return riskInfo.level;
};

/**
 * Get risk level key for styling (lowercase)
 * @param {number|string} riskValue - The risk value to classify
 * @returns {string} Risk level key for CSS styling
 */
export const getRiskLevelKey = (riskValue) => {
  const riskInfo = getRiskLevelInfo(riskValue);
  return riskInfo.key;
};

/**
 * Get risk level color
 * @param {number|string} riskValue - The risk value to classify
 * @returns {string} Hex color code for the risk level
 */
export const getRiskLevelColor = (riskValue) => {
  const riskInfo = getRiskLevelInfo(riskValue);
  return riskInfo.color;
};

/**
 * Check if risk value is valid and parseable
 * @param {number|string} riskValue - The risk value to validate
 * @returns {boolean} True if the risk value is valid
 */
export const isValidRiskValue = (riskValue) => {
  const riskInfo = getRiskLevelInfo(riskValue);
  return riskInfo.isValid;
};

// Export risk level constants for external use
export const RISK_LEVEL_CONSTANTS = RISK_LEVELS;
